import React, { useState, useMemo } from 'react';
import { 
  ReportConfig, 
  ReportColumn, 
  ReportFilter, 
  ReportDataSource,
  DateRangePreset 
} from '../../types';
import { getAvailableColumns, DATA_SOURCE_LABELS } from '../../utils/reportTemplates';
import './ReportBuilder.css';

interface ReportBuilderProps {
  config: ReportConfig;
  onConfigChange: (config: ReportConfig) => void;
  onPreview: () => void;
}

const DATE_RANGE_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this-week', label: 'This Week' },
  { value: 'last-week', label: 'Last Week' },
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'this-quarter', label: 'This Quarter' },
  { value: 'last-quarter', label: 'Last Quarter' },
  { value: 'this-year', label: 'This Year' },
  { value: 'last-year', label: 'Last Year' },
  { value: 'all-time', label: 'All Time' },
];

const FILTER_OPERATORS = [
  { value: 'equals', label: 'Equals' },
  { value: 'not-equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'greater-than', label: 'Greater Than' },
  { value: 'less-than', label: 'Less Than' },
  { value: 'between', label: 'Between' },
];

export const ReportBuilder: React.FC<ReportBuilderProps> = ({
  config,
  onConfigChange,
  onPreview,
}) => {
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  const availableColumns = useMemo(() => 
    getAvailableColumns(config.dataSource),
    [config.dataSource]
  );

  const updateConfig = (updates: Partial<ReportConfig>) => {
    onConfigChange({
      ...config,
      ...updates,
      updatedAt: new Date(),
    });
  };

  const handleDataSourceChange = (dataSource: ReportDataSource) => {
    const newColumns = getAvailableColumns(dataSource);
    updateConfig({
      dataSource,
      columns: newColumns.slice(0, 5).map(c => ({ ...c, visible: true })),
      filters: [],
    });
  };

  const toggleColumn = (field: string) => {
    const existingIndex = config.columns.findIndex(c => c.field === field);
    if (existingIndex >= 0) {
      // Remove column
      updateConfig({
        columns: config.columns.filter(c => c.field !== field),
      });
    } else {
      // Add column
      const columnDef = availableColumns.find(c => c.field === field);
      if (columnDef) {
        updateConfig({
          columns: [...config.columns, { ...columnDef, visible: true }],
        });
      }
    }
  };

  const addFilter = () => {
    const firstColumn = availableColumns[0];
    const newFilter: ReportFilter = {
      field: firstColumn.field,
      operator: 'equals',
      value: '',
    };
    updateConfig({
      filters: [...config.filters, newFilter],
    });
  };

  const updateFilter = (index: number, updates: Partial<ReportFilter>) => {
    const newFilters = [...config.filters];
    newFilters[index] = { ...newFilters[index], ...updates };
    updateConfig({ filters: newFilters });
  };

  const removeFilter = (index: number) => {
    updateConfig({
      filters: config.filters.filter((_, i) => i !== index),
    });
  };

  const handleDateRangeChange = (preset: DateRangePreset | 'custom') => {
    if (preset === 'custom') {
      updateConfig({
        dateRange: { start: new Date(), end: new Date() },
      });
    } else {
      updateConfig({
        dateRange: preset,
      });
    }
  };

  const isColumnSelected = (field: string) => 
    config.columns.some(c => c.field === field);

  return (
    <div className="report-builder">
      {/* Report Name */}
      <div className="builder-section">
        <label>Report Name</label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => updateConfig({ name: e.target.value })}
          placeholder="Enter report name"
          className="report-name-input"
        />
      </div>

      {/* Data Source */}
      <div className="builder-section">
        <label>Data Source</label>
        <div className="data-source-selector">
          {(Object.keys(DATA_SOURCE_LABELS) as ReportDataSource[]).map(source => (
            <button
              key={source}
              className={`source-btn ${config.dataSource === source ? 'active' : ''}`}
              onClick={() => handleDataSourceChange(source)}
            >
              {DATA_SOURCE_LABELS[source]}
            </button>
          ))}
        </div>
      </div>

      {/* Date Range */}
      <div className="builder-section">
        <label>Date Range</label>
        <div className="date-range-selector">
          <select
            value={typeof config.dateRange === 'string' ? config.dateRange : 'custom'}
            onChange={(e) => handleDateRangeChange(e.target.value as DateRangePreset | 'custom')}
          >
            {DATE_RANGE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
            <option value="custom">Custom Range</option>
          </select>
          
          {typeof config.dateRange === 'object' && (
            <div className="custom-date-range">
              <input
                type="date"
                value={config.dateRange.start?.toISOString().split('T')[0] || ''}
                onChange={(e) => updateConfig({
                  dateRange: {
                    ...(config.dateRange as { start: Date; end: Date }),
                    start: new Date(e.target.value),
                  },
                })}
              />
              <span>to</span>
              <input
                type="date"
                value={config.dateRange.end?.toISOString().split('T')[0] || ''}
                onChange={(e) => updateConfig({
                  dateRange: {
                    ...(config.dateRange as { start: Date; end: Date }),
                    end: new Date(e.target.value),
                  },
                })}
              />
            </div>
          )}
        </div>
      </div>

      {/* Columns */}
      <div className="builder-section">
        <div className="section-header">
          <label>Columns</label>
          <button 
            className="add-column-btn"
            onClick={() => setShowColumnPicker(!showColumnPicker)}
          >
            {showColumnPicker ? 'Done' : '+ Add Column'}
          </button>
        </div>
        
        {showColumnPicker && (
          <div className="column-picker">
            {availableColumns.map(col => (
              <label key={col.field} className="column-option">
                <input
                  type="checkbox"
                  checked={isColumnSelected(col.field)}
                  onChange={() => toggleColumn(col.field)}
                />
                <span className="column-label">{col.label}</span>
                <span className="column-type">{col.type}</span>
              </label>
            ))}
          </div>
        )}

        <div className="selected-columns">
          {config.columns.length === 0 ? (
            <p className="no-columns">No columns selected. Click "Add Column" to choose columns.</p>
          ) : (
            config.columns.map((col, index) => (
              <div key={col.field} className="selected-column">
                <span className="column-drag">≡</span>
                <span className="column-name">{col.label}</span>
                <span className="column-type-badge">{col.type}</span>
                <button 
                  className="remove-column-btn"
                  onClick={() => toggleColumn(col.field)}
                >
                  ×
                </button>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="builder-section">
        <div className="section-header">
          <label>Filters</label>
          <button className="add-filter-btn" onClick={addFilter}>
            + Add Filter
          </button>
        </div>
        
        {config.filters.length === 0 ? (
          <p className="no-filters">No filters applied. Click "Add Filter" to filter your data.</p>
        ) : (
          <div className="filters-list">
            {config.filters.map((filter, index) => (
              <div key={index} className="filter-row">
                <select
                  value={filter.field}
                  onChange={(e) => updateFilter(index, { field: e.target.value })}
                >
                  {availableColumns.map(col => (
                    <option key={col.field} value={col.field}>{col.label}</option>
                  ))}
                </select>
                <select
                  value={filter.operator}
                  onChange={(e) => updateFilter(index, { operator: e.target.value as any })}
                >
                  {FILTER_OPERATORS.map(op => (
                    <option key={op.value} value={op.value}>{op.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  value={filter.value || ''}
                  onChange={(e) => updateFilter(index, { value: e.target.value })}
                  placeholder="Value"
                />
                <button 
                  className="remove-filter-btn"
                  onClick={() => removeFilter(index)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sorting & Grouping */}
      <div className="builder-section">
        <label>Sorting</label>
        <div className="sort-options">
          <select
            value={config.sortBy?.[0]?.column || ''}
            onChange={(e) => updateConfig({
              sortBy: e.target.value 
                ? [{ column: e.target.value, direction: config.sortBy?.[0]?.direction || 'desc' }]
                : undefined,
            })}
          >
            <option value="">No sorting</option>
            {config.columns.map(col => (
              <option key={col.field} value={col.field}>{col.label}</option>
            ))}
          </select>
          
          {config.sortBy?.[0]?.column && (
            <select
              value={config.sortBy[0].direction}
              onChange={(e) => updateConfig({
                sortBy: [{ 
                  column: config.sortBy![0].column, 
                  direction: e.target.value as 'asc' | 'desc' 
                }],
              })}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          )}
        </div>
      </div>

      {/* Grouping */}
      <div className="builder-section">
        <label>Group By</label>
        <select
          value={config.groupBy?.[0] || ''}
          onChange={(e) => updateConfig({
            groupBy: e.target.value ? [e.target.value] : undefined,
          })}
        >
          <option value="">No grouping</option>
          {config.columns
            .filter(col => col.type === 'string')
            .map(col => (
              <option key={col.field} value={col.field}>{col.label}</option>
            ))}
        </select>
      </div>

      {/* Actions */}
      <div className="builder-actions">
        <button 
          className="preview-btn"
          onClick={onPreview}
          disabled={config.columns.length === 0}
        >
          Preview Report
        </button>
      </div>
    </div>
  );
};

export default ReportBuilder;
